import { writeThirdPartyRegistrationPayload } from '../utils/third-party-login'

type ThirdPartyProvider = 'google' | 'apple' | 'wechat' | 'telegram' | 'linkedin'

type TelegramAuthPayload = {
  auth_date?: number | string
  id?: number | string
  first_name?: string
  last_name?: string
  hash?: string
  photo_url?: string
}

const LINKEDIN_CLIENT_ID = '86xsd1vfot5q51'

const getThirdPartyNodeData = (response: any, routeName: string) => response?.usercenter?.[routeName]?.data || null

const isFalseLike = (value: unknown) => value === false || value === 0 || String(value).toLowerCase() === 'false'

const getThirdPartyLoginType = (payload: Record<string, any>) =>
  String(payload.loginType || payload.accountType || payload.LoginType || payload.AccountType || '')

const hasThirdPartyRegisterIdentity = (payload: Record<string, any>) =>
  Boolean(payload.id || payload.openId || payload.OpenId || payload.userId || payload.email || payload.Email)

/** 确保后端下发的领英授权地址始终使用当前站点的客户端 ID。 */
const replaceLinkedInClientId = (baseUrl: string) => {
  try {
    const linkedInUrl = new URL(baseUrl)
    linkedInUrl.searchParams.set('client_id', LINKEDIN_CLIENT_ID)
    return linkedInUrl.toString()
  } catch {
    return baseUrl
  }
}

export const useThirdPartyLogin = () => {
  const appStore = useAppStore()
  const { accessCookie, setCookieFromThirdParty } = useLogin()

  const { invokeV3 } = useLoginApi()

  const thirdPartyUrls = ref<Record<string, string>>({})
  const loadingProvider = ref<ThirdPartyProvider | ''>('')
  const appleReady = ref(false)

  /** 将未注册身份安全暂存，并在当前页面打开绑定邮箱弹窗。 */
  const openThirdPartyBindEmail = (payload: Record<string, any>) => {
    if (!import.meta.client) return

    if (
      writeThirdPartyRegistrationPayload({
        id: String(payload.id || payload.openId || payload.OpenId || payload.userId || ''),
        accountType: getThirdPartyLoginType(payload) || String(payload._provider || ''),
        email: String(payload.email || payload.Email || ''),
        token: String(payload.token || payload.Token || ''),
      })
    ) {
      appStore.setLoginShow(true)
    }
  }

  /** 直接把已完成授权的第三方结果落地为当前站点登录态。 */
  const loginThirdPartyDirectly = async (payload: Record<string, any>) => {
    if (payload.cookieValue) {
      await setCookieFromThirdParty(String(payload.cookieValue), {
        userId: payload.userId || '',
        nick: payload.nickName || payload.nick || '',
        avatar: payload.img || payload.avatar || '',
      })
      return true
    }

    if (payload.userId || payload.LoginToken) {
      return Boolean(await accessCookie(payload))
    }

    return false
  }

  const handleThirdPartyResult = async (payload: Record<string, any> | null | undefined, provider?: string) => {
    if (!payload) return

    if (provider) {
      payload._provider = provider
    }

    const registerValue = payload.isRegister ?? payload.IsRegister ?? payload.isregister
    if (isFalseLike(registerValue)) {
      openThirdPartyBindEmail(payload)
      return
    }

    if (await loginThirdPartyDirectly(payload)) {
      return
    }

    if (hasThirdPartyRegisterIdentity(payload)) {
      openThirdPartyBindEmail(payload)
    }
  }

  const getWechatHref = () => {
    if (!import.meta.client) return ''

    const baseUrl = thirdPartyUrls.value.Wechat || ''
    if (!baseUrl) return ''

    const returnUrl = appStore.allHost || window.location.href
    const middlepageUrl = new URL(returnUrl)
    middlepageUrl.search = ''
    middlepageUrl.hash = ''

    return `${baseUrl}&fromurl=${encodeURIComponent(returnUrl)}&middlepage=${encodeURIComponent(middlepageUrl.href)}`
  }

  const handleWechatLogin = () => {
    const href = getWechatHref()
    if (!href || !import.meta.client) return

    if (appStore.userlogin2) {
      window.parent?.postMessage({ op_type: 'opennew', url: href, parm: '' }, '*')
      return
    }

    window.location.href = href
  }

  /** 生成领英授权地址，授权完成后直接回到发起登录的当前页面。 */
  const getLinkedInHref = () => {
    if (!import.meta.client) return ''

    const baseUrl = replaceLinkedInClientId(thirdPartyUrls.value.linkedIn || '')
    if (!baseUrl) return ''

    const returnUrl = appStore.allHost || window.location.href
    const middlepageUrl = new URL(returnUrl)
    middlepageUrl.search = ''
    middlepageUrl.hash = ''

    return `${baseUrl}&fromurl=${encodeURIComponent(returnUrl)}&middlepage=${encodeURIComponent(middlepageUrl.href)}`
  }

  const handleLinkedInLogin = () => {
    const href = getLinkedInHref()
    if (!href || !import.meta.client) return

    if (appStore.userlogin2) {
      window.parent?.postMessage({ op_type: 'opennew', url: href, parm: '' }, '*')
      return
    }

    window.location.href = href
  }

  const handleGoogleAuthSuccess = async (credential = '') => {
    if (!credential) return

    loadingProvider.value = 'google'
    const result = await invokeV3('usercenter$googlecallback', { Token: credential }).catch(() => null)

    await handleThirdPartyResult(getThirdPartyNodeData(result, 'googlecallback'), 'google')
    loadingProvider.value = ''
  }

  const handleTelegramAuthSuccess = async (payload: TelegramAuthPayload) => {
    if (!payload?.id || !payload?.hash) return

    loadingProvider.value = 'telegram'
    const telegramPayload = {
      auth_date: payload?.auth_date,
      id: payload?.id,
      first_name: payload?.first_name,
      last_name: payload?.last_name,
      hash: payload?.hash,
      photo_url: payload?.photo_url || '',
    }
    const result = await invokeV3('usercenter$telegramcallback', telegramPayload).catch(() => null)
    await handleThirdPartyResult(getThirdPartyNodeData(result, 'telegramcallback'), 'telegram')
    loadingProvider.value = ''
  }

  const initApple = () => {
    if (!import.meta.client) return

    const AppleID = window.AppleID
    if (!AppleID?.auth) return

    AppleID.auth.init({
      clientId: 'com.wikibit.www',
      scope: 'name email',
      redirectURI: window.location.origin,
      state: 'xcoiv98y2kd22vusuye3kch',
      usePopup: true,
    })
    appleReady.value = true
  }

  const loadApple = () => {
    if (!import.meta.client) return

    if (window.AppleID?.auth) {
      initApple()
      return
    }

    if (document.querySelector('script[data-wikibit-apple-login]')) return

    const script = document.createElement('script')
    script.src = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js'
    script.async = true
    script.dataset.wikibitAppleLogin = 'true'
    script.addEventListener('load', initApple)
    document.head.appendChild(script)
  }

  const handleAppleLogin = async () => {
    if (!import.meta.client) return

    if (!appleReady.value) {
      loadApple()
      return
    }

    const AppleID = window.AppleID
    if (!AppleID?.auth) return

    try {
      loadingProvider.value = 'apple'
      const data = await AppleID.auth.signIn()
      const authorizationCode = data?.authorization?.code || ''
      const result = await invokeV3('usercenter$webapplecallback', {
        code: authorizationCode,
      }).catch(() => null)
      await handleThirdPartyResult(getThirdPartyNodeData(result, 'webapplecallback'), 'apple')
    } catch {
      // User closed the popup.
    } finally {
      loadingProvider.value = ''
    }
  }

  const handleSocialLogin = async (provider: ThirdPartyProvider) => {
    if (provider === 'wechat') {
      handleWechatLogin()
      return
    }

    if (provider === 'google') {
      return
    }

    if (provider === 'telegram') {
      return
    }

    if (provider === 'linkedin') {
      handleLinkedInLogin()
      return
    }

    await handleAppleLogin()
  }

  const initThirdPartyLogin = async () => {
    loadApple()

    const result = await invokeV3('usercenter$getbitwechatloginurl', {}).catch(() => null)
    const items = result?.usercenter?.getbitwechatloginurl?.data || []
    const nextMap: Record<string, string> = {}
    items.forEach((item: Record<string, string>) => {
      if (item.LoginType && item.LoginUrl) {
        nextMap[item.LoginType] = item.LoginUrl
      }
    })
    thirdPartyUrls.value = nextMap
  }

  return {
    handleGoogleAuthSuccess,
    handleSocialLogin,
    handleTelegramAuthSuccess,
    initThirdPartyLogin,
    loadingProvider,
  }
}
